import {NgModule} from '@angular/core';
import {BrowserModule} from '@angular/platform-browser';
import {FormsModule, ReactiveFormsModule} from '@angular/forms';
import {NgbModule} from '@ng-bootstrap/ng-bootstrap';
import {HTTP_INTERCEPTORS, HttpClient, HttpClientModule} from '@angular/common/http';

import 'reflect-metadata';

import {AppRoutingModule} from "./app-routing.module";
import {AppComponent} from "./app.component";
import {AdminHeaderComponent} from "./admin/header/header.component";
import {NavMenuComponent} from "./admin/header/nav-menu/nav-menu.component";
import {MsgBannerComponent} from './admin/header/msg-banner/msg-banner.component';
import {DashboardComponent} from './admin/dashboard/dashboard.component';
import {SigninComponent} from './admin/signin/signin.component';
import {SignoutComponent} from './admin/signout/signout.component';
import {SignupComponent} from './admin/signup/signup.component';
import {HttpAuthInterceptor} from "./services/http-auth.interceptor";
import {CookieService} from "ngx-cookie-service";
import {ListChannelsComponent} from './admin/channels/list/list.component';
import {AdminChannelsViewChannelComponent} from './admin/channels/view/view.component';
import {ChannelsListSectionComponent} from "./admin/channels/list/section/section.component";
import {ViewChannelStatusComponent} from './admin/channels/view/status/status.component';
import {AdminChannelViewDescriptionComponent} from './admin/channels/view/description/description.component';
import {AdminChannelViewInputInputComponent} from './admin/channels/view/input/input.component';
import {AdminChannelViewFieldsFieldsComponent} from './admin/channels/view/fields/fields.component';
import {TabSectionComponent} from './admin/templates/tab-section/tab-section.component';
import {ModalComponent} from './admin/templates/modal/modal.component';
import {AdminDashboardPipelinesComponent} from './admin/dashboard/pipelines/pipelines.component';
import {MachineComponent} from './admin/dashboard/machine/machine.component';
import {SettingsComponent} from './admin/dashboard/machine/settings/settings.component';
import {BatchesComponent} from './admin/dashboard/machine/batches/batches.component';
import {AdminChannelsViewVariablesComponent} from './admin/channels/view/variables/variables.component';
import {AdminChannelsViewLabelsComponent} from './admin/channels/view/labels/labels.component';
import {AdminChannelsViewReferencesComponent} from './admin/channels/view/references/references.component';
import {AdminChannelViewCommonRuleComponent} from './admin/channels/view/common/rule/rule.component';
import {AdminChannelsViewRulesComponent} from './admin/channels/view/rules/rules.component';
import {TranslateLoader, TranslateModule} from '@ngx-translate/core';
import {TranslateHttpLoader} from '@ngx-translate/http-loader';
import {AdminChannelsViewProfilesComponent} from './admin/channels/view/profiles/profiles.component';
import {AdminChannelViewCommonProfileComponent} from './admin/channels/view/common/profile/profile.component';
import {AdminLookupCommonChecksComponent} from './admin/lookup/common/checks/checks.component';
import {AdminLookupCommonRulesComponent} from './admin/lookup/common/rules/rules.component';
import {SingleCommonResultComponent} from './admin/lookup/common/result/result.component';
import {AdminLookupCommonFactorComponent} from './admin/lookup/common/result/factors/factors.component';
import {SingleCommonResultFlagComponent} from './admin/lookup/common/result/flags/flags.component';
import {AdminLookupCommonFormComponent} from "./admin/lookup/common/form/form.component";
import {AdminLookupCommonEntryComponent} from "./admin/lookup/common/entry/entry.component";
import {AdminLookupCommonTabsComponent} from "./admin/lookup/common/tabs/tabs.component";
import {AdminChannelsViewChecksComponent} from "./admin/channels/view/checks/checks.component";
import {
  SingleCommonResultFlagScreeningComponent
} from './admin/lookup/common/result/flags/screening/screening.component';
import {SingleCommonResultFlagScoringComponent} from './admin/lookup/common/result/flags/scoring/scoring.component';
import {
  SingleCommonResultFlagMonitoringComponent
} from './admin/lookup/common/result/flags/monitoring/monitoring.component';
import {JsonForTablePipe} from './pipes/json-for-table.pipe';
import {DecisionsComponent} from './admin/decisions/decisions.component';
import {ModelComponent} from './admin/decisions/model/model.component';
import {DataComponent} from './admin/decisions/model/data/data.component';
import {ExpectationComponent} from './admin/decisions/model/expectation/expectation.component';
import {EvolutionComponent} from './admin/decisions/model/evolution/evolution.component';
import {
  ApexAxisChartSeries,
  ApexDataLabels,
  ApexFill,
  ApexGrid,
  ApexLegend,
  ApexMarkers,
  ApexStroke,
  ApexTitleSubtitle,
  ApexTooltip,
  ApexXAxis,
  ApexYAxis,
  NgApexchartsModule
} from 'ng-apexcharts';
import {RealtimeComponent} from './admin/dashboard/machine/realtime/realtime.component';
import {TenantsComponent} from './admin/tenants/tenants.component';
import {AdminTenantsListComponent} from './admin/tenants/list/list.component';
import {AdminTenantsManageComponent} from './admin/tenants/manage/manage.component';
import {HashLocationStrategy, LocationStrategy, NgOptimizedImage} from "@angular/common";
import {SmartTooltipDirective} from './directives/smart-tooltip/smart-tooltip.directive';
import {AdminLookupScreeningComponent} from './admin/lookup/screening/screening.component';
import {AdminLayoutStandardComponent} from './admin/templates/layout/standard/standard.component';
import {TableStandardComponent} from './admin/templates/table/standard/standard.component';
import {TableCollapsableComponent} from './admin/templates/table/collapsable/collapsable.component';
import {AdminLookupScoringComponent} from './admin/lookup/scoring/scoring.component';
import {AdminLookupMonitoringComponent} from './admin/lookup/monitoring/monitoring.component';
import {AdminPageFirstComponent} from './admin/templates/page/first/first.component';
import {AdminPageSecondComponent} from './admin/templates/page/second/second.component';
import {SmallComponent} from './admin/templates/frontend/small/small.component';
import {LargeComponent} from './admin/templates/frontend/large/large.component';
import {ParametersComponent} from './admin/templates/table/parameters/parameters.component';
import {AdminSectionStandardComponent} from './admin/templates/section/standard/standard.component';
import {AdminPanelConfigurableComponent} from './admin/templates/panel/configurable/configurable.component';
import {AdminPanelDetailsComponent} from './admin/templates/panel/details/details.component';
import {AdminPanelEmptyComponent} from './admin/templates/panel/empty/empty.component';
import {AdminPanelDefaultComponent} from './admin/templates/panel/default/default.component';
import {AdminPanelStandardComponent} from './admin/templates/panel/standard/standard.component';
import {TableSpanningComponent} from './admin/templates/table/spanning/spanning.component';
import {StandardComponent} from './admin/templates/accordion/standard/standard.component';
import {EmptyComponent} from './admin/templates/section/empty/empty.component';
import {RuleHeaderComponent} from './admin/channels/view/common/rule-header/rule-header.component';
import {RuleDetailsComponent} from './admin/channels/view/common/rule-details/rule-details.component';
import {AdminLookupReportingComponent} from './admin/lookup/reporting/reporting.component';
import {AdminLayoutMinimalComponent} from './admin/templates/layout/minimal/minimal.component';
import {AdminLookupCommonShareComponent} from './admin/lookup/common/share/share.component';
import {CardComponent} from './admin/templates/panel/card/card.component';
import {AdminPanelInnerComponent} from './admin/templates/panel/inner/inner.component';
import {AdminAccordionTableComponent} from './admin/templates/accordion/table/table.component';
import {NiceComponent} from './admin/templates/accordion/nice/nice.component';
import {ModalFocusFixDirective} from './directives/modal-focus-fix/modal-focus-fix.directive';
import {AdminLookupAlertScreeningComponent} from './admin/lookup/common/result/alerts/screening/screening.component';
import {AdminLookupFactorScreeningComponent} from './admin/lookup/common/result/factors/screening/screening.component';
import {AdminLookupFactorScoringComponent} from './admin/lookup/common/result/factors/scoring/scoring.component';
import {
  AdminLookupFactorMonitoringComponent
} from './admin/lookup/common/result/factors/monitoring/monitoring.component';
import {OcrComponent} from './admin/templates/panel/ocr/ocr.component';
import {AdminResultsCasesComponent} from './admin/results/cases/cases.component';
import {AdminResultsCaseFormComponent} from './admin/results/cases/form/form.component';
import {AdminResultsCaseListComponent, NgbdSortableHeader} from './admin/results/cases/list/list.component';
import {AdminResultsCaseViewComponent} from './admin/results/cases/view/view.component';
import { AdminPanelConfirmComponent } from './admin/templates/panel/confirm/confirm.component';
import { AdminPanelResultComponent } from './admin/templates/panel/result/result.component';
import { ServicesComponent } from './admin/channels/view/services/services.component';
import { JsonOrEmptyPipe } from './pipes/json-or-empty.pipe';

// AoT requires an exported function for factories
export function HttpLoaderFactory(httpClient: HttpClient) {
  return new TranslateHttpLoader(httpClient);
}

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: any; //ApexChart;
  dataLabels: ApexDataLabels;
  markers: ApexMarkers;
  title: ApexTitleSubtitle;
  fill: ApexFill;
  yaxis: ApexYAxis;
  xaxis: ApexXAxis;
  tooltip: ApexTooltip;
  stroke: ApexStroke;
  grid: ApexGrid;
  colors: any;
  toolbar: any;
  labels: any;
  legend: ApexLegend;
};

@NgModule({
  declarations: [
    AppComponent,
    AdminHeaderComponent,
    NavMenuComponent,
    DashboardComponent,
    SigninComponent,
    SignoutComponent,
    SignupComponent,
    MsgBannerComponent,
    ListChannelsComponent,
    ChannelsListSectionComponent,
    AdminChannelsViewChannelComponent,
    ViewChannelStatusComponent,
    AdminChannelViewDescriptionComponent,
    AdminChannelViewInputInputComponent,
    AdminChannelViewFieldsFieldsComponent,
    TabSectionComponent,
    ModalComponent,
    AdminDashboardPipelinesComponent,
    MachineComponent,
    SettingsComponent,
    BatchesComponent,
    AdminChannelsViewChecksComponent,
    AdminChannelsViewVariablesComponent,
    AdminChannelsViewLabelsComponent,
    AdminChannelsViewReferencesComponent,
    AdminChannelViewCommonRuleComponent,
    AdminChannelsViewRulesComponent,
    AdminChannelsViewProfilesComponent,
    AdminChannelViewCommonProfileComponent,
    AdminLookupCommonChecksComponent,
    AdminLookupCommonRulesComponent,
    SingleCommonResultComponent,
    AdminLookupCommonFactorComponent,
    SingleCommonResultFlagComponent,
    AdminLookupCommonFormComponent,
    AdminLookupCommonEntryComponent,
    AdminLookupCommonTabsComponent,
    SingleCommonResultFlagScreeningComponent,
    SingleCommonResultFlagScoringComponent,
    SingleCommonResultFlagMonitoringComponent,
    JsonForTablePipe,
    AdminPanelDefaultComponent,
    AdminPanelStandardComponent,
    DecisionsComponent,
    ModelComponent,
    DataComponent,
    ExpectationComponent,
    EvolutionComponent,
    RealtimeComponent,
    TenantsComponent,
    AdminTenantsListComponent,
    AdminTenantsManageComponent,
    SmartTooltipDirective,
    AdminLookupScreeningComponent,
    AdminLayoutStandardComponent,
    TableStandardComponent,
    TableCollapsableComponent,
    AdminLookupScoringComponent,
    AdminLookupMonitoringComponent,
    AdminPageFirstComponent,
    AdminPageSecondComponent,
    SmallComponent,
    LargeComponent,
    ParametersComponent,
    AdminSectionStandardComponent,
    AdminPanelConfigurableComponent,
    AdminPanelDetailsComponent,
    AdminPanelEmptyComponent,
    TableSpanningComponent,
    StandardComponent,
    EmptyComponent,
    RuleHeaderComponent,
    RuleDetailsComponent,
    AdminLookupReportingComponent,
    AdminLayoutMinimalComponent,
    AdminLookupCommonShareComponent,
    CardComponent,
    AdminPanelInnerComponent,
    AdminAccordionTableComponent,
    NiceComponent,
    ModalFocusFixDirective,
    AdminLookupAlertScreeningComponent,
    AdminLookupFactorScreeningComponent,
    AdminLookupFactorScoringComponent,
    AdminLookupFactorMonitoringComponent,
    OcrComponent,
    AdminResultsCasesComponent,
    AdminResultsCaseFormComponent,
    AdminResultsCaseListComponent,
    AdminResultsCaseViewComponent,
    NgbdSortableHeader,
    AdminPanelConfirmComponent,
    AdminPanelResultComponent,
    ServicesComponent,
    JsonOrEmptyPipe,
  ],
  imports: [
    BrowserModule,
    ReactiveFormsModule,
    FormsModule,
    NgbModule,
    HttpClientModule,
    AppRoutingModule,
    TranslateModule.forRoot({
      defaultLanguage: "en",
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      }
    }),
    NgApexchartsModule,
    NgOptimizedImage
  ],
  providers: [
    CookieService,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpAuthInterceptor,
      multi: true
    },
    {
      provide: LocationStrategy,
      useClass: HashLocationStrategy
    }
  ],
  bootstrap: [AppComponent]
})
export class AppModule {
}
